// Channel comparison — Meta vs Google across the book for the selected range.
// Money display-currency aware; the "which channel is cheaper" verdict only
// exists when both CPLs resolve to ONE comparable figure (converted, or a
// single shared native currency) — never from a raw cross-currency blend.

import type { SpineDay } from '../../types/advertisingHome';
import type { FxRateSnapshot } from '../../types/fxRates';
import { sumTotals, type WindowTotals } from '../../lib/advertisingHome/spine';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { PanelTitle } from '../reporting/PanelTitle';
import { ChannelChip } from './AlertBadges';
import { formatLeads, homeMoney, moneyScalar } from './homeMoney';

function cplScalar(
  w: WindowTotals,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): { value: number; currency: string } | null {
  const spend = moneyScalar(w.spendByCurrency, display, snapshot);
  if (!spend || w.leads <= 0) return null;
  return { value: spend.value / w.leads, currency: spend.currency };
}

export function ChannelComparison({
  rows,
  display,
  snapshot,
}: {
  rows: SpineDay[];
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
}): React.JSX.Element {
  const meta = sumTotals(
    rows.filter((r) => r.channel === 'meta').map((r) => ({ ...r, date: null })),
  );
  const google = sumTotals(
    rows.filter((r) => r.channel === 'google').map((r) => ({ ...r, date: null })),
  );
  const metaCpl = cplScalar(meta, display, snapshot);
  const googleCpl = cplScalar(google, display, snapshot);

  let verdict: string | null = null;
  if (metaCpl && googleCpl && metaCpl.currency === googleCpl.currency) {
    const cheaper = metaCpl.value <= googleCpl.value ? 'Meta' : 'Google';
    const ratio = Math.max(metaCpl.value, googleCpl.value) / Math.min(metaCpl.value, googleCpl.value);
    verdict = `${cheaper} is the cheaper channel this range (${Math.round((ratio - 1) * 100)}% below the other's CPL).`;
  }

  const line = (label: 'meta' | 'google', w: WindowTotals, cpl: typeof metaCpl) => (
    <div className="grid grid-cols-[4.5rem_1fr_1fr_1fr] items-baseline gap-2 text-sm">
      <ChannelChip channel={label} />
      <span className="tabular-nums">{homeMoney(w.spendByCurrency, display, snapshot)}</span>
      <span className="tabular-nums">{formatLeads(w.leads)}</span>
      <span className="tabular-nums">
        {cpl ? formatReportingAmount(cpl.value, cpl.currency) : '—'}
      </span>
    </div>
  );

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <PanelTitle className="mb-2 block">Meta vs Google — this range</PanelTitle>
      <div className="mb-1 grid grid-cols-[4.5rem_1fr_1fr_1fr] gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <span />
        <span>Spend</span>
        <span>Leads</span>
        <span>CPL</span>
      </div>
      <div className="space-y-1.5">
        {line('meta', meta, metaCpl)}
        {line('google', google, googleCpl)}
      </div>
      {verdict && <p className="mt-2 text-xs text-muted-foreground">{verdict}</p>}
    </div>
  );
}
